import type { MonthlyPeriod, MonthlyPeriodCloseSummary, MonthlyPeriodStatus } from '../types';
import { monthKey as monthKeyForDate, normalizeDateISO } from './date';

export const MONTHLY_PERIOD_STATUSES: MonthlyPeriodStatus[] = ['OPEN', 'REVIEWED', 'CLOSED', 'REOPENED'];

const validMonthKey = (value: string) => /^\d{4}-\d{2}$/.test(value);

export function resolveFinancialMonthKey(date: string) {
  const iso = normalizeDateISO(date);
  return monthKeyForDate(iso);
}

export function normalizeMonthlyPeriodStatus(status?: string): MonthlyPeriodStatus {
  const normalized = String(status || 'OPEN').toUpperCase();
  return MONTHLY_PERIOD_STATUSES.includes(normalized as MonthlyPeriodStatus) ? normalized as MonthlyPeriodStatus : 'OPEN';
}

export function defaultMonthlyPeriod(uid: string, inputMonthKey: string): MonthlyPeriod {
  const key = validMonthKey(inputMonthKey) ? inputMonthKey : resolveFinancialMonthKey(inputMonthKey);
  return {
    id: key,
    uid,
    monthKey: key,
    status: 'OPEN',
    warningCountAtClose: 0,
    criticalCountAtClose: 0,
    closeCount: 0
  };
}

export function isMonthMutable(status?: string) {
  return normalizeMonthlyPeriodStatus(status) !== 'CLOSED';
}

export function monthLabel(inputMonthKey: string) {
  const key = validMonthKey(inputMonthKey) ? inputMonthKey : resolveFinancialMonthKey(inputMonthKey);
  const date = new Date(`${key}-01T00:00:00`);
  return date.toLocaleString('en-IN', { month: 'long', year: 'numeric' });
}

export function closedMonthMessage(inputMonthKey: string) {
  return `This financial period is closed. Reopen ${monthLabel(inputMonthKey)} from Monthly Close before making changes dated in this month.`;
}

export function assertMonthMutable(inputMonthKey: string, status?: string) {
  const key = validMonthKey(inputMonthKey) ? inputMonthKey : resolveFinancialMonthKey(inputMonthKey);
  if (!isMonthMutable(status)) throw new Error(closedMonthMessage(key));
}

export function assertFinancialDateMutableStatus(date: string, period?: Pick<MonthlyPeriod, 'monthKey' | 'status'> | null) {
  const key = resolveFinancialMonthKey(date);
  assertMonthMutable(key, period?.status || 'OPEN');
}

export function assertFinancialMutationMutableStatus(originalDate: string, nextDate: string | undefined, periods: Map<string, Pick<MonthlyPeriod, 'monthKey' | 'status'> | null>) {
  const keys = new Set([resolveFinancialMonthKey(originalDate), resolveFinancialMonthKey(nextDate || originalDate)]);
  keys.forEach((key) => assertMonthMutable(key, periods.get(key)?.status || 'OPEN'));
}

export function canMarkReviewed(status?: string) {
  return normalizeMonthlyPeriodStatus(status) !== 'CLOSED';
}

export function canClosePeriod(status: string | undefined, summary: Pick<MonthlyPeriodCloseSummary, 'criticalCountAtClose'>) {
  return normalizeMonthlyPeriodStatus(status) !== 'CLOSED' && Number(summary.criticalCountAtClose || 0) === 0;
}

export function canReopenPeriod(status?: string, reason?: string) {
  return normalizeMonthlyPeriodStatus(status) === 'CLOSED' && String(reason || '').trim().length >= 3;
}
